import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, Wheat, Grape, Mail, KeyRound } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function RegisterPage() {
  const { registerRequestOtp, registerVerify, registerComplete } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email | otp | password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [ticket, setTicket] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e) => {
    e && e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("कृपया वैध ई-मेल पत्ता प्रविष्ट करा.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const res = await registerRequestOtp(email.trim().toLowerCase());
      setInfo(res.message || "OTP पाठवला आहे.");
      setStep("otp");
    } catch (err) {
      setError(err.message || "OTP पाठवण्यात अडचण आली.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("कृपया ६ अंकी OTP प्रविष्ट करा.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const res = await registerVerify(email.trim().toLowerCase(), otp);
      setTicket(res.ticket);
      setInfo("");
      setStep("password");
    } catch (err) {
      setError(err.message || "OTP पडताळणी अयशस्वी.");
    } finally {
      setBusy(false);
    }
  };

  const complete = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("पासवर्ड किमान ६ अक्षरांचा असावा.");
      return;
    }
    if (password !== confirm) {
      setError("पासवर्ड जुळत नाही.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await registerComplete(email.trim().toLowerCase(), ticket, password);
      navigate("/apply");
    } catch (err) {
      setError(err.message || "खाते तयार करताना अडचण आली.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/20";
  const amberCls =
    "w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[15px] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/20";
  const labelCls = "mb-1.5 block text-[13px] font-bold tracking-wide text-slate-500";

  const heading = step === "otp" ? "ई-मेल पडताळणी" : step === "password" ? "पासवर्ड तयार करा" : "Register";
  const sub =
    step === "otp"
      ? `${email} वर पाठवलेला ६ अंकी OTP प्रविष्ट करा`
      : step === "password"
      ? "तुमच्या खात्यासाठी सुरक्षित पासवर्ड तयार करा"
      : "प्रथम तुमचा ई-मेल पडताळा";

  const StepDots = () => (
    <div className="mt-4 flex items-center gap-2" data-testid="register-steps">
      {["email", "otp", "password"].map((s, i) => {
        const active = ["email", "otp", "password"].indexOf(step) >= i;
        return <span key={s} className={`h-1.5 flex-1 rounded-full ${active ? "bg-[#1b5e3f]" : "bg-slate-200"}`} />;
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f6f2e6]">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-10 md:grid-cols-2 md:py-16">
        <div className="order-2 rounded-[1.75rem] border border-[#e6dcc4] bg-white p-8 shadow-[0_16px_50px_rgba(27,94,63,0.10)] sm:p-10">
          <div className="mb-2 flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1b5e3f] text-white">
              <Wheat className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="font-latin text-[15px] font-bold text-[#1b5e3f]">ASSK Amravati</span>
          </div>
          <h1 className="font-head text-[34px] font-medium text-[#1b5e3f]">{heading}</h1>
          <p className="mt-1 text-[15px] text-[#6b7a60]">{sub}</p>
          <StepDots />

          {step === "email" && (
            <form onSubmit={requestOtp} className="mt-7 space-y-5" data-testid="register-email-form">
              <div>
                <label className={labelCls}>E-MAIL ADDRESS</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoFocus placeholder="you@example.com" data-testid="register-email-input" className={`${amberCls} pl-11`} />
                </div>
                <p className="mt-2 text-[12px] text-slate-400">या ई-मेलवर ६ अंकी OTP पाठवला जाईल.</p>
              </div>
              {error && <p className="text-[13px] text-red-600" data-testid="register-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="request-otp-btn" className="w-full rounded-full bg-[#1b5e3f] py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#144d33] disabled:opacity-60">
                {busy ? "OTP पाठवत आहे..." : "OTP मिळवा"}
              </button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verify} className="mt-7 space-y-5" data-testid="register-otp-form">
              {info && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700" data-testid="otp-info">{info}</p>}
              <div>
                <label className={labelCls}>OTP (6 अंकी)</label>
                <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoFocus placeholder="______" data-testid="otp-input" className="w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-[22px] font-bold tracking-[0.5em] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/20" />
                <p className="mt-2 text-[12px] text-slate-400">OTP ५ मिनिटांसाठी वैध आहे.</p>
              </div>
              {error && <p className="text-[13px] text-red-600" data-testid="otp-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="verify-otp-btn" className="w-full rounded-full bg-[#1b5e3f] py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#144d33] disabled:opacity-60">
                {busy ? "पडताळणी होत आहे..." : "OTP पडताळा"}
              </button>
              <div className="flex items-center justify-between text-[13px]">
                <button type="button" onClick={() => { setStep("email"); setError(""); setOtp(""); }} className="font-semibold text-slate-500 hover:text-slate-700" data-testid="otp-back-btn">← ई-मेल बदला</button>
                <button type="button" onClick={requestOtp} disabled={busy} className="font-semibold text-[#1b5e3f] hover:underline disabled:opacity-60" data-testid="otp-resend-btn">OTP पुन्हा पाठवा</button>
              </div>
            </form>
          )}

          {step === "password" && (
            <form onSubmit={complete} className="mt-7 space-y-5" data-testid="register-password-form">
              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700">
                <ShieldCheck className="h-4 w-4" /> {email} पडताळला गेला ✓
              </div>
              <div>
                <label className={labelCls}>PASSWORD <span className="text-slate-400">*</span></label>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoFocus placeholder="किमान ६ अक्षरे" data-testid="password-input" className={`${amberCls} pl-11 pr-11`} />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelCls}>CONFIRM PASSWORD <span className="text-slate-400">*</span></label>
                <input type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="पुन्हा पासवर्ड टाका" data-testid="confirm-password-input" className={inputCls} />
              </div>
              {error && <p className="text-[13px] text-red-600" data-testid="password-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="create-account-btn" className="w-full rounded-full bg-[#1b5e3f] py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#144d33] disabled:opacity-60">
                {busy ? "खाते तयार होत आहे..." : "खाते तयार करा"}
              </button>
            </form>
          )}
        </div>

        {/* promo panel */}
        <div className="relative order-1 flex flex-col justify-center overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-[#1b5e3f] to-[#2d7a52] p-9 text-white md:p-11">
          <div className="pointer-events-none absolute -right-6 -top-6 opacity-15"><Grape className="h-40 w-40" strokeWidth={1.2} /></div>
          <div className="pointer-events-none absolute bottom-4 right-6 opacity-15"><Wheat className="h-24 w-24" strokeWidth={1.2} /></div>
          <span className="relative w-fit rounded-full bg-[#E0A106] px-4 py-1.5 text-[12px] font-bold tracking-wide text-[#1b3d2a] font-latin">RETURNING APPLICANT</span>
          <h2 className="font-head relative mt-5 text-[38px] font-medium leading-tight">
            Already started your <br />
            <span className="text-[#F4CE6A]">ASSK Application?</span>
          </h2>
          <p className="relative mt-4 max-w-md text-[16px] leading-relaxed text-[#d7ebda]">
            Sign in to continue exactly where you left off. Your saved progress and application status are waiting for you.
          </p>
          <div className="relative mt-8 flex max-w-md items-center gap-3 rounded-2xl bg-white/10 p-4 ring-1 ring-white/20 backdrop-blur">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E0A106] text-[#1b3d2a]"><ShieldCheck className="h-5 w-5" /></div>
            <div>
              <p className="text-[15px] font-bold text-white">Pick up where you left off</p>
              <p className="text-[14px] text-[#cfe6d2]">Your saved answers are ready and waiting</p>
            </div>
          </div>
          <button onClick={() => navigate("/login")} className="relative mt-6 w-fit rounded-full bg-[#E0A106] px-7 py-3 text-[15px] font-semibold text-[#1b3d2a] transition-colors hover:bg-[#c88f05]">
            Sign In to Portal
          </button>
        </div>
      </div>
    </div>
  );
}
